import { useRef, useState } from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { Button } from './button';
import { Label } from './label';
import { toast } from '@/hooks/use-toast';
import { uploadFileToS3ViaPreSignedUrl } from '@/utils';
import { useCognitoAuth } from '@/hooks/useCognitoAuth';
import { Loader2, Upload, X } from 'lucide-react';

type ImageUploadProps<T extends FieldValues = FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label?: string;
  keyPrefix: string;
  description?: string;
  maxFileSize?: number; // in bytes
  acceptedFormats?: string[];
};

export function ImageUpload<T extends FieldValues = FieldValues>({
  control,
  name,
  label,
  keyPrefix,
  description,
  maxFileSize = 5242880, // 5MB default
  acceptedFormats = ['.jpg', '.jpeg', '.png'],
}: ImageUploadProps<T>) {
  const { field } = useController({
    control,
    name: name as Path<T>,
  });
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getAccessToken } = useCognitoAuth();

  const currentImages = (field.value as string[]) || [];

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of files) {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!acceptedFormats.some((fmt) => extension === fmt.toLowerCase())) {
        errors.push(`${file.name}: Invalid file type. Accepted: ${acceptedFormats.join(', ')}`);
        continue;
      }

      if (file.size > maxFileSize) {
        errors.push(`${file.name}: File too large. Max size: ${maxFileSize / 1024 / 1024}MB`);
        continue;
      }

      validFiles.push(file);
    }

    if (errors.length > 0) {
      toast({
        title: 'Validation Error',
        description: errors.join('\n'),
        variant: 'destructive',
      });
    }

    if (validFiles.length === 0) return;

    const newUrls: string[] = [...currentImages];
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      setUploadingIndex(i);
      try {
        const url = await uploadFileToS3ViaPreSignedUrl({
          file,
          keyPrefix,
          getAccessToken,
        });
        newUrls.push(url);
      } catch (error) {
        toast({
          title: 'Upload Error',
          description: `Failed to upload ${file.name}`,
          variant: 'destructive',
        });
      }
    }

    field.onChange(newUrls);
    setUploadingIndex(null);

    if (validFiles.length > 0) {
      toast({
        title: 'Success',
        description: `Successfully uploaded ${validFiles.length} image(s)`,
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = (index: number) => {
    const newUrls = currentImages.filter((_, i) => i !== index);
    field.onChange(newUrls);
  };

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats.join(',')}
            onChange={handleFileSelect}
            disabled={uploadingIndex !== null}
            multiple
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingIndex !== null}
            className="flex items-center gap-2"
          >
            {uploadingIndex !== null ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Add Images
              </>
            )}
          </Button>
        </div>

        {currentImages.length > 0 && (
          <div className="grid grid-cols-4 gap-4">
            {currentImages.map((url, index) => (
              <div key={index} className="relative group">
                <div className="aspect-square rounded-md border overflow-hidden bg-muted">
                  <img
                    src={url}
                    alt={`Upload ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => handleRemove(index)}
                  className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
